/**
 * O plano deste workspace: o que está valendo, até quando, e **quanto do
 * limite já foi usado de verdade**.
 *
 * Por que o consumo fica aqui e não só na vitrine: a pergunta que a pessoa faz
 * nesta tela não é "quanto custa o Black", é "por que não consigo importar mais
 * um grupo". `usage` responde isso com o número que o servidor contou
 * (`3 de 3`), e não com o que a tabela de preços promete.
 *
 * Duas coisas que esta tela se recusa a fazer:
 *  - **esconder exceção negociada.** Limite com `source: 'override'` aparece
 *    nomeado. Existe override ativo em produção; um número que não bate com o
 *    plano anunciado, sem explicação, vira "o sistema está errado";
 *  - **fingir cobrança.** Trocar de plano aqui registra um pedido. Não há
 *    gateway: ninguém é cobrado, e a tela diz isso antes e depois do clique.
 */
import { useState } from 'react';
import { Link } from 'react-router';
import {
  Badge,
  Button,
  buttonVariants,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  Skeleton,
  cn,
  NO_DATA_DASH,
} from '@mgdlead/ui';
import { can } from '@mgdlead/shared';
import { CreditCard, ExternalLink, Gauge } from 'lucide-react';

import { AvisoDePagamento, PedidoRegistrado } from './AvisoDePagamento';
import { BarraDeUso } from './BarraDeUso';
import { CicloSelector } from './CicloSelector';
import { PlanoCard } from './PlanoCard';
import {
  acaoDoPlano,
  algumCheckoutDisponivel,
  checkoutDoCiclo,
  estadoDaAssinatura,
  rotuloDeCiclo,
  rotuloDoBotao,
} from './billing-ui';
import { useWorkspace } from '../workspace/WorkspaceContext';
import { FormAlert } from '@mgdlead/ui';
import {
  useBillingCatalog,
  useBillingCurrent,
  useRequestPlanChange,
  useStartTrial,
} from '../../lib/api';
import type { BillingInterval, CatalogPlan } from '../../lib/api';
import { formatExact } from '../../lib/datetime';
import { humanizeError } from '../../lib/errors';

/** O ciclo que o seletor abre marcado enquanto o usuário não escolher outro. */
const CICLO_INICIAL: BillingInterval = 'MONTHLY';

interface PedidoFeito {
  plano: string;
  ciclo: string | null;
}

export function PlanoDoWorkspace() {
  const { workspace, role } = useWorkspace();
  const atual = useBillingCurrent(workspace.id);
  const catalogo = useBillingCatalog();

  const [interval, setInterval] = useState<BillingInterval>(CICLO_INICIAL);
  const [pedido, setPedido] = useState<PedidoFeito | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const startTrial = useStartTrial();
  const requestChange = useRequestPlanChange();
  const podeMexer = can(role, 'billing:manage');

  const pedirTroca = async (plan: CatalogPlan) => {
    setErro(null);
    setPedido(null);
    try {
      if (acaoDoPlano(plan) === 'trial') {
        await startTrial.mutateAsync({ workspaceId: workspace.id, planCode: plan.code });
        setPedido({ plano: plan.name, ciclo: null });
        return;
      }
      await requestChange.mutateAsync({ workspaceId: workspace.id, planCode: plan.code, interval });
      setPedido({ plano: plan.name, ciclo: rotuloDeCiclo(interval) });
    } catch (causa) {
      setErro(humanizeError(causa));
    }
  };

  const pendente = startTrial.isPending || requestChange.isPending;

  return (
    <div className="flex flex-col gap-4">
      <PlanoVigente query={atual} timezone={workspace.timezone} />
      <Consumo query={atual} />

      {/* ------------------------------ trocar ------------------------------ */}
      <Card className="flex flex-col gap-4">
        <CardHeader>
          <CardTitle>Trocar de plano</CardTitle>
          <CardDescription>
            Escolha o plano e o ciclo. O pedido fica registrado e alguém entra em contato.
          </CardDescription>
        </CardHeader>

        {!podeMexer ? (
          <FormAlert tone="info">
            Seu papel ({role}) permite ver o plano, mas quem contrata e troca a assinatura é o dono
            do workspace.
          </FormAlert>
        ) : (
          <AvisoDePagamento
            checkoutDisponivel={algumCheckoutDisponivel(catalogo.data?.plans ?? [], interval)}
          />
        )}

        {erro !== null ? <FormAlert>{erro}</FormAlert> : null}
        {pedido !== null ? <PedidoRegistrado plano={pedido.plano} ciclo={pedido.ciclo} /> : null}

        {catalogo.isPending ? (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full max-sm:hidden" />
            <Skeleton className="h-64 w-full max-xl:hidden" />
          </div>
        ) : catalogo.isError ? (
          <ErrorState
            compact
            title="Não foi possível carregar os planos"
            description="Sem a tabela do servidor não dá para mostrar preço nenhum — e preço inventado na tela é pior do que preço nenhum."
            onRetry={() => void catalogo.refetch()}
          />
        ) : catalogo.data.plans.length === 0 ? (
          <EmptyState
            kind="no-data"
            compact
            icon={CreditCard}
            title="Nenhum plano publicado"
            description="O servidor não devolveu plano público nenhum. Fale com o suporte."
          />
        ) : (
          <>
            <CicloSelector
              plans={catalogo.data.plans}
              value={interval}
              onChange={setInterval}
              className="self-start"
            />

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {catalogo.data.plans.map((plan) => (
                <PlanoCard
                  key={plan.code}
                  plan={plan}
                  interval={interval}
                  featureKeys={catalogo.data.featureKeys}
                  atual={plan.code === atual.data?.plan.code}
                  acao={(() => {
                    const ehOAtual = plan.code === atual.data?.plan.code;
                    const checkout = checkoutDoCiclo(plan, interval);

                    /*
                      Com link da Kiwify no ar, o botão LEVA ao checkout — mas só
                      para quem pode contratar e só se não for o plano vigente.
                      Quem não tem `billing:manage` continua vendo o botão
                      desligado: mandar um ANALYST para uma página de pagamento
                      seria a tela contradizendo a regra que o servidor aplica.

                      O trial continua sendo mutação nossa, não checkout: ele não
                      cobra nada e a trava de "um por workspace" é do banco.
                    */
                    if (
                      checkout !== null &&
                      podeMexer &&
                      !ehOAtual &&
                      acaoDoPlano(plan) !== 'trial'
                    ) {
                      return (
                        <a
                          href={checkout}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={cn(
                            buttonVariants({
                              variant: plan.badge !== null ? 'primary' : 'secondary',
                              block: true,
                            }),
                          )}
                        >
                          {rotuloDoBotao(plan)}
                        </a>
                      );
                    }

                    return (
                      <Button
                        variant={plan.badge !== null ? 'primary' : 'secondary'}
                        block
                        disabled={!podeMexer || ehOAtual}
                        loading={pendente}
                        loadingLabel="Registrando pedido"
                        onClick={() => void pedirTroca(plan)}
                      >
                        {ehOAtual ? 'Plano atual' : rotuloDoBotao(plan)}
                      </Button>
                    );
                  })()}
                />
              ))}
            </div>

            <Link
              to="/planos"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ExternalLink aria-hidden="true" className="size-4" />
              Ver a comparação completa dos planos
            </Link>
          </>
        )}
      </Card>
    </div>
  );
}

/* ========================================================================== */
/*  O que está valendo                                                         */
/* ========================================================================== */

type ConsultaDoAtual = ReturnType<typeof useBillingCurrent>;

function PlanoVigente({ query, timezone }: { query: ConsultaDoAtual; timezone: string }) {
  if (query.isPending) {
    return (
      <Card className="flex flex-col gap-3">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-4 w-64" />
      </Card>
    );
  }

  if (query.isError) {
    return (
      <Card padded={false}>
        <ErrorState
          compact
          title="Não foi possível carregar seu plano"
          description="Enquanto isso, os limites continuam valendo no servidor — o que falhou foi só mostrar quais são."
          onRetry={() => void query.refetch()}
        />
      </Card>
    );
  }

  const { plan, subscription, trialAvailable } = query.data;
  const estado = estadoDaAssinatura(subscription?.status ?? null);
  const ciclo = rotuloDeCiclo(subscription?.interval ?? null);
  const fimDoTeste = formatExact(subscription?.trialEndsAt ?? null, timezone);
  const fimDoPeriodo = formatExact(subscription?.currentPeriodEnd ?? null, timezone);

  return (
    <Card className="flex flex-col gap-3">
      <CardHeader>
        <CardTitle>Seu plano</CardTitle>
        <CardDescription>O que está valendo neste workspace agora.</CardDescription>
      </CardHeader>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-2xl font-bold tracking-tight">{plan.name}</span>
        {plan.badge !== null ? (
          // `text-foreground`: ver o comentário em PlanoCard.tsx (contraste no tema escuro)
          <Badge tone="accent" dot className="text-foreground">
            {plan.badge}
          </Badge>
        ) : null}
        {estado !== null ? (
          <Badge tone={estado.tone} dot>
            {estado.label}
          </Badge>
        ) : (
          /* Nunca assinou não é um estado de assinatura — é a ausência dela. */
          <Badge tone="neutral" dot>
            Sem assinatura
          </Badge>
        )}
      </div>

      <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
        <Linha rotulo="Ciclo de cobrança" valor={ciclo} vazio="Sem ciclo ligado a este plano" />
        {subscription?.status === 'TRIALING' ? (
          <Linha rotulo="Teste grátis até" valor={fimDoTeste} vazio="Sem data de fim registrada" />
        ) : (
          <Linha rotulo="Vence em" valor={fimDoPeriodo} vazio="Sem data de vencimento" />
        )}
      </dl>

      {subscription === null ? (
        <p className="text-sm text-muted-foreground">
          Este workspace nunca teve assinatura
          {trialAvailable ? ' — o teste grátis ainda está disponível.' : '.'}
        </p>
      ) : null}
    </Card>
  );
}

function Linha({
  rotulo,
  valor,
  vazio,
}: {
  rotulo: string;
  valor: string | null;
  vazio: string;
}) {
  return (
    <div className="flex flex-col gap-0.5 border-t border-border pt-2">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{rotulo}</dt>
      <dd className={cn('text-sm', valor === null && 'text-muted-foreground')}>
        {valor ?? (
          <>
            <span aria-hidden="true" className="nums">
              {NO_DATA_DASH}
            </span>{' '}
            {vazio}
          </>
        )}
      </dd>
    </div>
  );
}

/* ========================================================================== */
/*  Consumo contra o limite                                                    */
/* ========================================================================== */

function Consumo({ query }: { query: ConsultaDoAtual }) {
  if (query.isPending) {
    return (
      <Card className="flex flex-col gap-3">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </Card>
    );
  }

  if (query.isError) return null;

  const { usage } = query.data;

  return (
    <Card className="flex flex-col gap-1">
      <CardHeader>
        <CardTitle>Consumo do plano</CardTitle>
        <CardDescription>
          O que este workspace já usa agora, contado pelo servidor — não o que a tabela promete.
        </CardDescription>
      </CardHeader>

      {usage.length === 0 ? (
        <EmptyState
          kind="no-data"
          compact
          icon={Gauge}
          title="Sem limites para mostrar"
          description="O servidor não devolveu nenhum limite avaliado para este workspace."
        />
      ) : (
        <div className="divide-y divide-border">
          {usage.map((row) => (
            <BarraDeUso key={row.key} row={row} />
          ))}
        </div>
      )}
    </Card>
  );
}
